import { useEffect, useMemo, useState } from "react";
import { SortOrder, TABLE_ACTIONS } from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { MainLayoutContainer } from "components/Layout";
import dayjs from "dayjs";

import {
  Box,
  Chip,
  IconButton,
  Link as LinkBase,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useGetFeedbacksByOrganizationIdLazyQuery,
  useGetFeedbacksLazyQuery,
  useUpdateFeedbackMutation,
} from "graphql/__generated__/hooks";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { shouldHideFeedback } from "utils/feedback";
import { Link, useParams } from "react-router-dom";
import { Info, OpenInNew } from "@mui/icons-material";
import { SubRequirementsModal } from "components/Feedback/SubRequirementsModal";
import {
  getFeedbackTypeLabel,
  getRequirementDisplayData,
} from "../../../utils/feedbackIter";
import { Requirement } from "types/requirement";
import { CoachPetAvatar } from "components/VetStore/CoachPetAvatar";

export const FeedbackList = () => {
  const [deleteFeedbackModal, setDeleteFeedbackModal] = useState(false);
  const [feedbackIdToDelete, setFeedbackIdToDelete] = useState("");
  const { organizationId } = useParams();
  const [openSubRequirementsModal, setOpenSubRequirementsModal] =
    useState(false);
  const [selectedIterData, setSelectedIterData] = useState<{
    iterName: string;
    subRequirements: Array<{
      requirement_id: string;
      name: string;
      description?: string | null;
    }>;
    ratingsByRequirement?: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    >;
  } | null>(null);
  const allRequirements: Requirement[] = [];

  const [getFeedbacks, { data: feedbacksData }] = useGetFeedbacksLazyQuery();
  const [
    getFeedbacksByOrganizationId,
    { data: feedbacksByOrganizationIdData },
  ] = useGetFeedbacksByOrganizationIdLazyQuery();
  const [updateFeedback] = useUpdateFeedbackMutation();
  const [showHidden, setShowHidden] = useState(false);

  const feedbackList = useMemo<FeedbackType[]>(() => {
    const list = organizationId
      ? (feedbacksByOrganizationIdData?.feedback as FeedbackType[]) || []
      : (feedbacksData?.feedback as FeedbackType[]) || [];
    if (showHidden) return list;
    return list.filter((feedback) => !shouldHideFeedback(feedback));
  }, [
    feedbacksData,
    feedbacksByOrganizationIdData,
    organizationId,
    showHidden,
  ]);

  useEffect(() => {
    if (organizationId) {
      getFeedbacksByOrganizationId({ variables: { organizationId } }).then();
    } else {
      getFeedbacks().then();
    }
  }, [organizationId]);

  const handleActionClick = async (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT:
        break;
      case TABLE_ACTIONS.DELETE:
        setFeedbackIdToDelete(id);
        setDeleteFeedbackModal(true);
        break;
      default:
        break;
    }
  };

  const refetch = () => {
    if (organizationId) {
      return getFeedbacksByOrganizationId({ variables: { organizationId } });
    }
    return getFeedbacks();
  };

  const handleDelete = async () => {
    if (!feedbackIdToDelete || feedbackIdToDelete === "") {
      return;
    }
    await updateFeedback({
      variables: {
        id: feedbackIdToDelete,
        feedback: {
          status: FeedbackStatus.HIDDEN,
        },
      },
    });
    await refetch();
    setFeedbackIdToDelete("");
  };

  const getStatusChip = (status: string) => {
    if (status === FeedbackStatus.DONE) {
      return <Chip label="Done" color="success" variant="filled" />;
    }
    if (status === FeedbackStatus.DECLINED) {
      return <Chip label="Declined" color="error" variant="filled" />;
    }
    if (status === FeedbackStatus.DRAFT) {
      return <Chip label="Draft" color="secondary" variant="outlined" />;
    }
    if (status === FeedbackStatus.HIDDEN) {
      return <Chip label="Hidden" color="secondary" variant="filled" />;
    }
    if (status === FeedbackStatus.ARCHIVED) {
      return <Chip label="Archived" color="secondary" variant="filled" />;
    }
    if (status === FeedbackStatus.EXPIRED) {
      return <Chip label="Expired" color="error" variant="filled" />;
    }
    return <Chip label="Pending" color="warning" variant="filled" />;
  };

  const getTypeLabel = (feedback: any): string =>
    getFeedbackTypeLabel(feedback, allRequirements);

  // Helper function to get type chip for a feedback
  const getTypeChip = (feedback: any) => {
    const label = getTypeLabel(feedback);
    if (!label) return null;

    let color: "primary" | "success" | "info" | "warning" = "primary";
    if (label === "EPA") {
      color = "primary";
    } else if (label === "Competencies") {
      color = "success";
    } else if (label === "ITER") {
      color = "info";
    }

    return <Chip label={label} size="small" color={color} />;
  };

  const handleFilter = (row: any, filterText: string) => {
    const filterLower = filterText.toLowerCase();
    
    // Filter by student name
    const matchesStudent = row.student?.name?.toLowerCase().includes(filterLower);
    
    // Filter by coach name
    const matchesCoach = row.coach?.name?.toLowerCase().includes(filterLower);
    
    // Filter by requirement names
    const matchesRequirement = row.feedback_skills?.some((skill: any) =>
      skill.requirement?.name?.toLowerCase().includes(filterLower),
    );
    
    // Filter by type (EPA, ITER, Competencies)
    const typeLabel = getTypeLabel(row);
    const matchesType = typeLabel.toLowerCase().includes(filterLower);
    
    // Filter by observation date
    let matchesObservationDate = false;
    if (row.observation_date) {
      const observationDate = dayjs(row.observation_date);
      const dateFormats = [
        observationDate.format("MMM D, YYYY"), // "Jan 28, 2026"
        observationDate.format("MMM DD, YYYY"), // "Jan 28, 2026"
        observationDate.format("MMMM D, YYYY"), // "January 28, 2026"
        observationDate.format("MMMM DD, YYYY"), // "January 28, 2026"
        observationDate.format("MM/DD/YYYY"), // "01/28/2026"
        observationDate.format("MM-DD-YYYY"), // "01-28-2026"
        observationDate.format("YYYY-MM-DD"), // "2026-01-28"
        observationDate.format("YYYY/MM/DD"), // "2026/01/28"
        observationDate.format("D MMM YYYY"), // "28 Jan 2026"
        observationDate.format("DD MMM YYYY"), // "28 Jan 2026"
        observationDate.format("YYYY"), // "2026"
        observationDate.format("MMM YYYY"), // "Jan 2026"
        observationDate.format("MMMM YYYY"), // "January 2026"
      ];
      matchesObservationDate = dateFormats.some((format) =>
        format.toLowerCase().includes(filterLower),
      );
    }
    
    // Filter by created date
    let matchesCreatedDate = false;
    if (row.created_at) {
      const createdDate = dayjs(row.created_at);
      const dateFormats = [
        createdDate.format("MMM D, YYYY"),
        createdDate.format("MMM DD, YYYY"),
        createdDate.format("MMMM D, YYYY"),
        createdDate.format("MMMM DD, YYYY"),
        createdDate.format("MM/DD/YYYY"),
        createdDate.format("MM-DD-YYYY"),
        createdDate.format("YYYY-MM-DD"),
        createdDate.format("YYYY/MM/DD"),
        createdDate.format("D MMM YYYY"),
        createdDate.format("DD MMM YYYY"),
        createdDate.format("YYYY"),
        createdDate.format("MMM YYYY"),
        createdDate.format("MMMM YYYY"),
      ];
      matchesCreatedDate = dateFormats.some((format) =>
        format.toLowerCase().includes(filterLower),
      );
    }
    
    return (
      matchesStudent ||
      matchesCoach ||
      matchesRequirement ||
      matchesType ||
      matchesObservationDate ||
      matchesCreatedDate
    );
  };

  return (
    <MainLayoutContainer title="Feedback">
      <>
        <CustomTable
          entities={feedbackList}
          handleActionClick={handleActionClick}
          fieldToFilter={handleFilter}
          filterOptions={{
            showHidden,
            onShowHiddenChange: setShowHidden,
          }}
          columns={[
            {
              id: "feedback_skills",
              label: "Requirements",
              value: (row: any) => {
                if (row.feedback_skills.length === 0) {
                  return "No requirements";
                }
                const requirementData = getRequirementDisplayData(
                  row,
                  allRequirements,
                );
                return (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {requirementData.name}
                    </Typography>
                    {requirementData.isITER && requirementData.iterData && (
                      <Tooltip title="View Competencies">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Build per-competency rating history for this ITER across the list
                            const ratingsByRequirement: Record<
                              string,
                              {
                                date: string;
                                learnerRating?: number | null;
                                coachRating?: number | null;
                              }[]
                            > = {};

                            const history = feedbackList
                              .filter((fb) => {
                                const d = getRequirementDisplayData(
                                  fb,
                                  allRequirements,
                                );
                                return (
                                  d.isITER &&
                                  d.name === requirementData.name
                                );
                              })
                              .sort((a, b) => {
                                const da = a.observation_date || a.created_at || "";
                                const db = b.observation_date || b.created_at || "";
                                return da.localeCompare(db);
                              });

                            history.forEach((fb) => {
                              const date = (fb.observation_date ||
                                fb.created_at) as string;
                              fb.feedback_skills.forEach((skill: any) => {
                                const reqId =
                                  skill.requirement?.requirement_id;
                                if (!reqId) return;
                                if (!ratingsByRequirement[reqId]) {
                                  ratingsByRequirement[reqId] = [];
                                }
                                ratingsByRequirement[reqId].push({
                                  date,
                                  learnerRating: skill.self_rating ?? null,
                                  coachRating: skill.coach_rating ?? null,
                                });
                              });
                            });

                            setSelectedIterData({
                              ...requirementData.iterData,
                              ratingsByRequirement,
                            });
                            setOpenSubRequirementsModal(true);
                          }}
                        >
                          <Info fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              },
              sort: (row: any) => {
                if (row.feedback_skills.length === 0) {
                  return "";
                }
                const requirementData = getRequirementDisplayData(
                  row,
                  allRequirements,
                );
                return requirementData.name || "";
              },
            },
            {
              id: "type",
              label: "Type",
              value: (row: any) => getTypeChip(row),
              sort: (row: any) => getTypeLabel(row),
            },
            {
              id: "student",
              label: "Learner",
              value: (row: any) => (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CoachPetAvatar
                    pictureUrl={row.student?.picture_url}
                    name={row.student?.name}
                    equippedStoreItems={row.student?.equipped_store_items}
                    avatarSize={28}
                  />
                  <Typography variant="body2">
                    {row.student?.name || "-"}
                  </Typography>
                </Stack>
              ),
              sort: (row: any) => row.student?.name || "",
            },
            {
              id: "coach",
              label: "Coach",
              value: (row: any) => (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CoachPetAvatar
                    pictureUrl={row.coach?.picture_url}
                    name={row.coach?.name}
                    equippedStoreItems={row.coach?.equipped_store_items}
                    avatarSize={28}
                  />
                  <Typography variant="body2">
                    {row.coach?.name || "-"}
                  </Typography>
                </Stack>
              ),
              sort: (row: any) => row.coach?.name || "",
            },
            {
              id: "observation_date",
              label: "Observation Date",
              value: "observation_date",
              sort: (row: any) => row.observation_date || "",
            },
            {
              id: "status",
              label: "Status",
              value: (row: any) => getStatusChip(row.status),
              sort: (row: any) => row.status || "",
            },
            {
              id: "view_feedback",
              value: (row: any) => (
                <Stack direction="row" spacing={2} alignItems="center">
                  <LinkBase
                    sx={{ cursor: "pointer" }}
                    component={Link}
                    to={
                      organizationId
                        ? `/organization/${organizationId}/feedback/${row.feedback_id}`
                        : `/feedback/${row.feedback_id}`
                    }
                  >
                    View
                  </LinkBase>

                  <Tooltip title="Open in new tab" placement="top">
                    <IconButton
                      component="a"
                      href={
                        organizationId
                          ? `/organization/${organizationId}/feedback/${row.feedback_id}`
                          : `/feedback/${row.feedback_id}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                    >
                      <OpenInNew />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            },
            { id: "actions", value: "" },
          ]}
          isSelectable={false}
          allowedMenuItems={[TABLE_ACTIONS.DELETE]}
          extraActionItems={[]}
          idColumn={"feedback_id"}
          sortDefault={SortOrder.desc}
          sortDefaultField={"observation_date"}
          enableCardViewInSmallDevices
        />

        {deleteFeedbackModal && (
          <ConfirmActionModal
            title={"Heads up!"}
            message={"Are you sure you want to delete this Feedback?"}
            show={deleteFeedbackModal}
            onConfirm={handleDelete}
            onDismiss={() => {
              setDeleteFeedbackModal(false);
            }}
          />
        )}

        {selectedIterData && (
          <SubRequirementsModal
            open={openSubRequirementsModal}
            onClose={() => {
              setOpenSubRequirementsModal(false);
              setSelectedIterData(null);
            }}
            iterName={selectedIterData.iterName}
            subRequirements={selectedIterData.subRequirements}
            ratingsByRequirement={selectedIterData.ratingsByRequirement}
          />
        )}
      </>
    </MainLayoutContainer>
  );
};
